// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Column, LgSpacing, MdSpacing, XxxlPadding } from '@alto/design-system';
import { AddOnsContext, ProgynyUnboxingGuide, useQueryEssentials, useUnboxingGuide } from '@alto/features';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ORIGINS } from '~shared/features/essentials/add_ons_consts';
import { type Prescription, type Shipment } from '~shared/types';
import { FullPage } from '~web/features/essentials/components';
import { DesktopProducts } from '~web/features/essentials/pages/OrderSuccess/DesktopProducts';
import { LastRefillCard } from '~web/features/essentials/pages/OrderSuccess/LastRefillCard';
import { MobileProducts } from '~web/features/essentials/pages/OrderSuccess/MobileProducts';
import { OrderSuccessHeader } from '~web/features/essentials/pages/OrderSuccess/OrderSuccessHeader';
import { useDeprecatedMediaQuery } from '~web/hooks';

const StyledDesktopProductsColumn = styled(Column)`
  overflow: hidden;
`;

// Mobile single-column gets a small horizontal inset so cards don't render edge-to-edge.
// Desktop two-column uses larger insets to match design.
const getDefaultViewSidePadding = (showDesktopProducts: boolean, isMobile: boolean) => {
  if (!showDesktopProducts) return isMobile ? SPACING.STATIC.MD : SPACING.STATIC.NONE;
  if (isMobile) return SPACING.STATIC.LG;
  return SPACING.STATIC.XXL;
};

// Right column on desktop matches the left column's 64px (XXXL) inset; nothing on mobile.
const getRightColumnSidePadding = (showDesktopProducts: boolean) =>
  showDesktopProducts ? SPACING.STATIC.XXXL : SPACING.STATIC.NONE;

type Props = {
  shipment: Shipment;
  isSplitOrder: boolean;
  lastRefillPrescriptions: Prescription[];
  hasCompound: boolean;
};

export const OrderSuccessWithProducts = ({ shipment, isSplitOrder, lastRefillPrescriptions, hasCompound }: Props) => {
  const addOnsContext = useMemo(
    () => ({ shipmentID: shipment?.id, origin: ORIGINS.ORDER_SUCCESS }) as const,
    [shipment?.id],
  );
  const isMobile = !useDeprecatedMediaQuery('SM');
  const showProducts = isSplitOrder || !hasCompound;
  // The right-hand essentials column on desktop should only take its 50% slot when there are
  // actually essentials categories to show. Without this check, `DesktopProducts` returns null
  // when categories.length === 0 but the parent still allocates the column — leaving the left
  // OrderSuccess content squashed into 50% with a blank 50% beside it. Optimistically reserve
  // the column while loading so the loading skeleton fills it; collapse to 100% once we know
  // there are no categories.
  const {
    isLoading: isEssentialsLoading,
    error: essentialsError,
    categories: essentialsCategories,
  } = useQueryEssentials({
    addressID: shipment?.address_id,
    date: shipment?.scheduled_date,
  });
  const hasEssentialsContent = !!essentialsCategories && essentialsCategories.length > 0;
  const willShowEssentials = isEssentialsLoading || (!essentialsError && hasEssentialsContent);
  const showDesktopProducts = !isMobile && showProducts && willShowEssentials;
  const showMobileProducts = isMobile && showProducts && willShowEssentials;
  const screenPartitions = isMobile || !showDesktopProducts ? '100%' : '50%';
  const { showUnboxingGuide } = useUnboxingGuide({ shipmentID: shipment?.id });

  const defaultViewSidePadding = getDefaultViewSidePadding(showDesktopProducts, isMobile);
  const sidePadding = getRightColumnSidePadding(showDesktopProducts);

  return (
    <FullPage>
      <AddOnsContext.Provider value={addOnsContext}>
        <Column
          flexBasis={screenPartitions}
          backgroundColor={COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST}
        >
          <XxxlPadding
            leftPadding={isMobile ? defaultViewSidePadding : undefined}
            rightPadding={defaultViewSidePadding}
          >
            <Column>
              <OrderSuccessHeader
                shipment={shipment}
                isSplitOrder={isSplitOrder}
              />
              <LgSpacing />
              {showUnboxingGuide && (
                <>
                  <LgSpacing />
                  <ProgynyUnboxingGuide />
                </>
              )}
              {lastRefillPrescriptions.length > 0 && <LastRefillCard prescriptions={lastRefillPrescriptions} />}
              {showMobileProducts ? (
                <>
                  <MdSpacing />
                  {shipment ? <MobileProducts /> : null}
                </>
              ) : null}
            </Column>
          </XxxlPadding>
        </Column>
        {showDesktopProducts && (
          <StyledDesktopProductsColumn flexBasis={screenPartitions}>
            <XxxlPadding
              leftPadding={SPACING.STATIC.XXL}
              rightPadding={sidePadding}
            >
              <DesktopProducts />
            </XxxlPadding>
          </StyledDesktopProductsColumn>
        )}
      </AddOnsContext.Provider>
    </FullPage>
  );
};
